import { useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import { Notice } from "@williampollardco/portal-ui";
import { paths } from "@shared-core/routing/routes";
import { useCustomerLinkAdapter } from "@shared-core/data/CustomerLinkAdapterContext";
import type { QuoteAccepted } from "@shared-core/data/customer-link-adapter";
import type { LinkBlock } from "@shared-core/domain/portal-sign-in/types";
import { AccountOfferCard } from "./AccountOfferCard";
import { BoldAddresses } from "./BoldAddresses";
import {
  GoneCard,
  LinkErrorCard,
  LinkLayout,
  LinkLoadingCard,
  LinkNotFoundCard,
} from "./LinkLayout";
import { QuotePageBody } from "./QuotePageBody";
import { useGoTo, useLinkRead } from "./useLinkRead";

/** `/quote/:token` (`?quote_no=` for a quote that replaced the link's own): the customer's quote by their
 *  emailed link, no sign-in. The token stays here; the page body only receives what each action does.
 *
 *  `linkBlock` is what the portal sign-in adds to the read (docs/features/portal-sign-in/08, 5.3): a session that can
 *  see the quote opens it in the portal; an order contact with an open invite is offered an account; a notice follows a
 *  sign-in back to the page. Another session in this browser is mentioned only on the sign-in screen (Ben, 2 Nov). */
export function QuoteLinkPage({
  linkBlock = null,
  createAccountHref = "/",
}: {
  linkBlock?: LinkBlock | null;
  createAccountHref?: string;
} = {}) {
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const quoteNo = searchParams.get("quote_no");
  const adapter = useCustomerLinkAdapter();
  const goTo = useGoTo();
  const [offerSkipped, setOfferSkipped] = useState(false);
  const { state, reload } = useLinkRead(
    `${token}|${quoteNo ?? ""}`,
    () => adapter.getQuote(token, quoteNo),
    "We couldn't load this quote.",
  );

  function onAccepted(accepted: QuoteAccepted) {
    // Card: Stripe's checkout, which returns to the order's page. Otherwise
    // straight to the order (`?placed=1` for its thank-you), or, with no link
    // to hand out, this page's accepted state.
    if (accepted.checkout_url) goTo(accepted.checkout_url);
    else if (accepted.order_url) goTo(`${accepted.order_url}?placed=1`);
    else void reload();
  }

  if (linkBlock?.opensAs === "signed_in" && linkBlock.recordPath) {
    return <Navigate to={linkBlock.recordPath} replace />;
  }
  const offer = offerSkipped ? null : (linkBlock?.accountOffer ?? null);

  return (
    <LinkLayout
      signedInWhere="your quotes"
      isWide
      // A landing notice only ever follows a sign-in from this page.
      isSignedIn={Boolean(linkBlock?.landingNotice)}
      legalDetails={state.kind === "ok" ? state.view.legal_details : null}
    >
      {linkBlock?.landingNotice ? (
        <div className="mb-4">
          <Notice tone="ok">
            <BoldAddresses text={linkBlock.landingNotice} />
          </Notice>
        </div>
      ) : null}
      {state.kind === "loading" ? (
        <LinkLoadingCard what="quote" />
      ) : state.kind === "error" ? (
        <LinkErrorCard
          what="quote"
          message={state.message}
          onRetry={() => void reload()}
        />
      ) : state.kind === "gone" ? (
        <GoneCard sentence={state.sentence} what="quotes" />
      ) : state.kind === "not_found" ? (
        <LinkNotFoundCard />
      ) : (
        <QuotePageBody
          // The offer is the one call to action: while it stands, the page reads as the staff mirror does, with nothing
          // to accept.
          page={offer ? { ...state.view, accept: null } : state.view}
          slot={
            offer ? (
              <AccountOfferCard
                offer={offer}
                signInHref={createAccountHref}
                onSkip={() => setOfferSkipped(true)}
              />
            ) : undefined
          }
          actions={
            offer
              ? null
              : {
                  accept: (input) => adapter.acceptQuote(token, input),
                  onAccepted,
                  decline: (reason) =>
                    adapter.declineQuote(token, quoteNo, reason),
                  reload,
                  writes: adapter.artwork,
                  fetchFile: adapter.fetchFile,
                  pdfUrl: state.view.pdf_url,
                }
          }
          replacementHref={(replacing) =>
            paths.portalQuoteLink({ token, quoteNo: replacing })
          }
        />
      )}
    </LinkLayout>
  );
}
